import React, { useState } from 'react';
import { Plane, Calendar, TrendingDown, Sparkles, HelpCircle } from 'lucide-react';

/**
 * FlightAlternatesRail — adjacent-date flight alternatives strip.
 *
 * iter56 / Phase B. When the backend's flight_alternates.find_better_adjacent_dates
 * found a notably better same-route option on ±3 days, we surface it as a
 * one-tap pill below the flight list. Pattern lifted from Google Flights'
 * "nearby dates" tip + Skyscanner's "save €X by flying a day earlier".
 *
 * iter56.1 — added a "Why?" hover tooltip on each pill showing the exact
 * math behind the savings ("€220 × 2 − €150 × 2 = €140 saved") so users
 * trust the discount without trying to recompute it themselves.
 *
 * Props:
 *   alternates — array of { date, returnDate, price, priceNumeric,
 *                           basePrice, basePriceLabel, travelers,
 *                           savings, savingsTotal, route, direct,
 *                           label, promoteReason }
 *   onPick(alt) — optional click handler. When present, clicking the pill
 *                 sends a follow-up chat query asking the AI to switch dates.
 */
export default function FlightAlternatesRail({ alternates, onPick }) {
  // Hooks must run before any early return.
  const [openTipIndex, setOpenTipIndex] = useState(null);

  if (!alternates || !Array.isArray(alternates) || alternates.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 mb-2" data-testid="flight-alternates-rail">
      <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-wider text-emerald-700 font-semibold px-1">
        <Sparkles className="w-3 h-3" />
        Smarter date options
      </div>
      <div className="flex flex-wrap gap-2">
        {alternates.map((alt, i) => {
          const Icon = alt.promoteReason === 'direct' ? Plane : TrendingDown;
          const handleClick = onPick ? () => onPick(alt) : undefined;
          const Wrapper = onPick ? 'button' : 'div';
          const showTip = openTipIndex === i;
          return (
            <div key={`flight-alt-${i}-${alt.date}`} className="relative">
              <Wrapper
                type={onPick ? 'button' : undefined}
                onClick={handleClick}
                data-testid={`flight-alternate-${i}`}
                className={`
                  flex items-start gap-2 p-3 pr-9 rounded-xl border text-left transition-all
                  ${onPick ? 'hover:shadow-md hover:border-emerald-300 cursor-pointer' : ''}
                  bg-emerald-50/60 border-emerald-200 text-emerald-900
                `}
              >
                <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-xs font-bold">
                    <Calendar className="w-3 h-3 opacity-70" />
                    <span>{alt.date}</span>
                    {alt.returnDate && (
                      <span className="opacity-70">→ {alt.returnDate}</span>
                    )}
                  </div>
                  <div className="text-sm font-extrabold mt-0.5">{alt.label}</div>
                  <div className="text-[11px] mt-0.5 opacity-70">
                    {alt.route} · {alt.price}{alt.direct ? ' · direct' : ''}
                  </div>
                </div>
              </Wrapper>

              {/* "Why?" affordance — hover / focus reveals the savings math.
                  Stops propagation so clicking the tooltip doesn't fire
                  the pill's onPick handler. */}
              <button
                type="button"
                onMouseEnter={() => setOpenTipIndex(i)}
                onMouseLeave={() => setOpenTipIndex(null)}
                onFocus={() => setOpenTipIndex(i)}
                onBlur={() => setOpenTipIndex(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenTipIndex(showTip ? null : i);
                }}
                aria-label="Why this is cheaper"
                data-testid={`flight-alternate-${i}-why`}
                className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 flex items-center justify-center transition-colors"
              >
                <HelpCircle className="w-3 h-3" />
              </button>

              {showTip && (
                <div
                  role="tooltip"
                  data-testid={`flight-alternate-${i}-tooltip`}
                  className="absolute z-10 top-full left-0 right-0 mt-1 p-3 bg-white border border-emerald-200 rounded-xl shadow-lg text-[11px] text-gray-700 leading-relaxed"
                >
                  <div className="font-bold text-emerald-800 mb-1 text-xs">
                    {alt.promoteReason === 'direct' ? 'Why this is better' : 'Why this is cheaper'}
                  </div>
                  {alt.promoteReason === 'direct' ? (
                    <>
                      <div>Your selected date has a connecting flight.</div>
                      <div className="mt-1">
                        On <span className="font-semibold">{alt.date}</span> the same route is a{' '}
                        <span className="font-semibold">direct flight</span> at{' '}
                        <span className="font-semibold">{alt.price}</span>{alt.basePriceLabel ? <> vs. <span className="font-semibold">{alt.basePriceLabel}</span> base</> : null}.
                      </div>
                    </>
                  ) : (
                    <>
                      {alt.basePrice != null && alt.priceNumeric != null && (
                        <div>
                          Base flight:{' '}
                          <span className="font-semibold">€{Math.round(alt.basePrice)}</span>
                          {alt.travelers > 1 ? <> × {alt.travelers} travelers</> : null}
                          {' '}={' '}
                          <span className="font-semibold">
                            €{Math.round(alt.basePrice * (alt.travelers || 1))}
                          </span>
                        </div>
                      )}
                      {alt.priceNumeric != null && (
                        <div>
                          On {alt.date}:{' '}
                          <span className="font-semibold">€{Math.round(alt.priceNumeric)}</span>
                          {alt.travelers > 1 ? <> × {alt.travelers} travelers</> : null}
                          {' '}={' '}
                          <span className="font-semibold">
                            €{Math.round(alt.priceNumeric * (alt.travelers || 1))}
                          </span>
                        </div>
                      )}
                      <div className="mt-1 pt-1 border-t border-emerald-100">
                        You save:{' '}
                        <span className="font-bold text-emerald-700">
                          €{Math.round(alt.savingsTotal || alt.savings || 0)}
                        </span>
                        {alt.travelers > 1 && (
                          <> total (€{Math.round(alt.savings || 0)}/pax)</>
                        )}
                      </div>
                    </>
                  )}
                  <div className="mt-2 text-[10px] text-gray-400">
                    Prices pulled live from Aviasales · final cost confirmed on the booking page.
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
